import { Day, DayValue } from '@amir04lm26/react-modern-calendar-date-picker'

import {
  PatientDetailsDto,
  ProgramCatalogDto,
  ReconcileVialRequestDto,
  ShortOrderSummaryDto
} from '../../../types/swaggerTypes'

/** Options for patient-details refetch. */
export type GetPatientOptions = {
  /**
   * When true, refetch without toggling the page-level loading spinner.
   * Used for PTA return-from-Castor sync so the page does not visibly refresh
   * before webhook-driven consent/baseline updates arrive (CLOS-13951).
   */
  silent?: boolean
}

/**
 * Augmented orders response type for custom hook
 */
export type IAugmentOrdersResponse = {
  isLoading: boolean
  augmentedOrders: IPastOrderSummary[]
  patient: PatientDetailsDto | undefined
  isProgramClosed: boolean
  allowsDiscontinuation: boolean
  getPatient: (options?: GetPatientOptions) => void
}

/**
 * Calendar picker interface
 */
export interface ICalendarPicker {
  /** The selected day */
  selectedDay: DayValue
  /** The minimum day to show */
  minimumDate: Day
  /** The maximum day to show */
  maximumDate?: Day
  /** Whether calendar is open or not */
  isOpen: boolean
}

/**
 * Extend Vial to include all the information we need for the view
 * */
export interface IPatientVial extends ReconcileVialRequestDto {
  /** The order number for the vial */
  orderNumber: number
  /** Human readable date (actual Date is an ISO 8601 string YYYY-MM-DD) */
  dateAdministeredFormatted?: string
  /** A flag to show if the vial has been reconciled */
  wasReconciled?: boolean
  /** The vial creation date (ISO 8601 string) */
  vialCreationDate?: string
}

export interface VialRecordState {
  vialNumber: string
  batchNumber: string
  vialStatus: string
  dateAdministered: string
}

/**
 * Extend Order summary to include all the information we need for the view
 * */
export interface IPastOrderSummary extends ShortOrderSummaryDto {
  /** Show the row toggle */
  canBeReconciled: boolean
  /** The shipped date as a day */
  dayOrderShipped: Day
  /** The vials to reconcile */
  vialsToReconcile: IPatientVial[]
  /** Whether the table row is toggled open */
  isOrderToggledOpen: boolean
}

/**
 * Program for custom hook response
 */
export type IProgramResponse = {
  /** If the program is loading */
  isProgramLoading: boolean
  /** The provided program catalogue dto */
  program?: ProgramCatalogDto
}

/**
 *
 Reconciliation type for Vials
 */
export enum VialReconciliationType {
  /** Can only select ‘Administered’ or ‘Destroyed’ from Disposition */
  DispositionOnly = 'Disposition Only',
  /** Disposition and Batch - Can select ‘Administered’ or ‘Destroyed’ from Disposition and must complete a Batch Number */
  DispositionBatch = 'Disposition and Batch',
  /**  Disposition and Date - Can select ‘Administered’ or ‘Destroyed’ from Disposition and must complete a Date */
  DispositionDate = 'Disposition and Date',
  /** Disposition, Batch and Date - Can select ‘Administered’ or ‘Destroyed’ from Disposition and must complete a Date AND Batch Number */
  DispositionBatchAndDate = 'Disposition, Batch and Date'
}
